import { isString } from 'fitify-types/src/type-guards/common'
import {
  getMetaData,
  type MetaDataProps,
} from 'fitify-ui-onboarding/src/utils/meta-tags'
import type { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next/pages'

import { SubscriptionPage } from 'sections/subscription/SubscriptionPage'
import type { PageMeta } from 'types/page-meta'
import { Funnel } from 'types/state'
import { ONBOARDING_PATH } from 'utils/funnel-pages'
import { getCommonStaticProps } from 'utils/get-common-static-props'
import { getFunnelsWithRoute } from 'utils/routes'

const SUPPORTED_PATHS = [
  ...getFunnelsWithRoute('subscription'),
  ONBOARDING_PATH,
] as const

type SupportedPath = (typeof SUPPORTED_PATHS)[number]

const FALLBACK_PLAN_TITLE_KEY = 'paywall_your_plan_is_ready_calisthenics'

const getDefaultPlanTitleKey = (path: SupportedPath): string | undefined => {
  switch (path) {
    case Funnel.CHF01:
      return 'paywall_title_ch_f'
    case Funnel.CHM01:
      return 'paywall_title_ch_m'
    case Funnel.TCHM01:
      return 'paywall_your_plan_is_ready_taichi'
    case Funnel.HOPIF01:
      return 'paywall_your_plan_is_ready_home_pilates'
    case Funnel.WALLPIF01:
      return 'paywall_your_plan_is_ready_wallpi'
    case Funnel.MILICAF01:
    case Funnel.MILICAM01:
      return 'paywall_your_plan_is_ready_milica'
    default:
      return undefined
  }
}

const isSupportedPath = (value: unknown): value is SupportedPath => {
  return isString(value) && SUPPORTED_PATHS.some((path) => path === value)
}

type Props = {
  funnelId: SupportedPath
  metaData: MetaDataProps
  pageMeta: PageMeta
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const funnelId = context.params?.funnelId

  if (!isSupportedPath(funnelId)) {
    return { notFound: true }
  }

  const metaData = getMetaData(context.query.promo)

  return {
    props: {
      ...(await getCommonStaticProps(context)),
      funnelId,
      metaData,
      pageMeta: {
        titleTranslationKey:
          getDefaultPlanTitleKey(funnelId) ?? FALLBACK_PLAN_TITLE_KEY,
        metaData,
        canonical: true,
      },
    },
  }
}

export default function Page({ funnelId, metaData }: Props) {
  const { t } = useTranslation()
  const defaultPlanTitleKey = getDefaultPlanTitleKey(funnelId)

  return (
    <SubscriptionPage
      defaultPlanTitle={
        defaultPlanTitleKey ? t(defaultPlanTitleKey) : undefined
      }
      metaData={metaData}
    />
  )
}
